/**
 * useChatHistory — wire-up hook between the chat UI and the backend HTTP API.
 *
 * Responsibilities:
 *  - Loads chat history on mount (GET /api/v1/messages).
 *  - Sends text / voice messages, appending an optimistic user bubble.
 *  - Polls GET /api/v1/voice/result/{eventId} until terminal, then appends the
 *    mapped assistant bubble (AISummary / AIClarify / AIText).
 *  - Tags a resolved AIClarify bubble with `resolvedOptionId` on option pick.
 *  - Exposes `activeRun` — the in-flight command pass — so the voice-dock can
 *    show recognise/think/done stages. The dock and the chat screen consume one
 *    and the same hook instance: a single source of truth for the conversation.
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { humanizeError } from '../../api';
import { listMessages, sendText, sendVoice } from '../../api/endpoints/messages';
import { pollVoiceResult } from '../../api/endpoints/voice';
import type { AIClarifyOption, Message } from './Message';
import { makeOptimisticUser, resultToMessage, rowsToFeed } from './messageAdapter';

export interface UseChatHistoryOptions {
  readonly limit?: number;
  readonly pollIntervalMs?: number;
}

/** Стадия текущего прохода команды — для индикации в voice-dock. */
export type RunStage = 'recognizing' | 'thinking' | 'done' | 'error';

/**
 * In-flight (или только что завершённый) проход команды. Один на всё
 * приложение — voice-dock и экран чата читают один и тот же.
 */
export interface ActiveRun {
  /** Монотонный id прохода — отсекает гонки устаревших промисов. */
  readonly runId: number;
  readonly stage: RunStage;
  readonly inputKind: 'voice' | 'text';
  /** Слова пользователя; '' пока идёт распознавание голоса. */
  readonly transcript: string;
  /** id баббла-ответа в feed, когда стадия 'done'. */
  readonly replyId: string | null;
  /** Текст ошибки, когда стадия 'error' — сбой до ответа агента. */
  readonly error: string | null;
}

export interface UseChatHistory {
  readonly feed: readonly Message[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly activeRun: ActiveRun | null;
  refresh(): Promise<void>;
  sendText(text: string): Promise<void>;
  sendVoice(audio: Blob, filename?: string): Promise<void>;
  /**
   * Отправить текст, распознанный на устройстве нативно (Web Speech API,
   * aidepo-64). В отличие от sendVoice здесь нет аудио и STT уже выполнен на
   * клиенте: уходит на /messages/text с source='voice_native'. Прогон помечается
   * как голосовой (inputKind 'voice'), но сразу со стадии 'thinking' — стадии
   * 'recognizing' нет, текст уже готов.
   */
  sendVoiceNative(transcript: string): Promise<void>;
  pickClarifyOption(msgId: string, option: AIClarifyOption): Promise<void>;
  clearRun(): void;
}

/** Monotonic client-side id generator for optimistic/local-only bubbles. */
function makeLocalIdFactory(): () => string {
  let n = 0;
  return () => {
    n += 1;
    return `local-${Date.now().toString(36)}-${n}`;
  };
}

function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

export function useChatHistory(opts?: UseChatHistoryOptions): UseChatHistory {
  const limit = opts?.limit ?? 50;
  const pollIntervalMs = opts?.pollIntervalMs ?? 500;

  const [feed, setFeed] = useState<readonly Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const nextId = useRef<() => string>(makeLocalIdFactory());
  const runSeq = useRef<number>(0);

  // Single AbortController whose lifetime is the hook's; aborts in-flight
  // polling loops on unmount.
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMessages(limit);
      setFeed(rowsToFeed(res.messages));
    } catch (e) {
      setError(toError(e));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const clearRun = useCallback((): void => setActiveRun(null), []);

  /**
   * Shared tail: poll a freshly-submitted eventId to terminal status and append
   * the mapped assistant bubble. Returns the appended Message, or null when the
   * poll was aborted (hook unmounted). A failed agent job is still a terminal
   * result and yields a (returned) AIText bubble.
   */
  const awaitAgentReply = useCallback(
    async (eventId: string): Promise<Message | null> => {
      const signal = abortRef.current?.signal;
      try {
        const result = await pollVoiceResult(eventId, {
          intervalMs: pollIntervalMs,
          ...(signal !== undefined ? { signal } : {}),
        });
        const msg = resultToMessage(nextId.current(), result);
        setFeed((prev) => [...prev, msg]);
        return msg;
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return null;
        const msg: Message = {
          id: nextId.current(),
          kind: 'ai_text',
          text: humanizeError(e, 'agent'),
        };
        setFeed((prev) => [...prev, msg]);
        return msg;
      }
    },
    [pollIntervalMs],
  );

  const finishRun = useCallback((runId: number, replyId: string): void => {
    setActiveRun((r) =>
      r !== null && r.runId === runId ? { ...r, stage: 'done', replyId } : r,
    );
  }, []);

  const failRun = useCallback((runId: number, message: string): void => {
    setActiveRun((r) =>
      r !== null && r.runId === runId ? { ...r, stage: 'error', error: message } : r,
    );
  }, []);

  const doSendText = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      const runId = (runSeq.current += 1);
      setFeed((prev) => [...prev, makeOptimisticUser(nextId.current(), trimmed)]);
      setActiveRun({
        runId,
        stage: 'thinking',
        inputKind: 'text',
        transcript: trimmed,
        replyId: null,
        error: null,
      });
      setError(null);
      try {
        const res = await sendText({ text: trimmed });
        const reply = await awaitAgentReply(res.eventId);
        if (reply !== null) finishRun(runId, reply.id);
      } catch (e) {
        const err = toError(e);
        setError(err);
        failRun(runId, err.message);
      }
    },
    [awaitAgentReply, finishRun, failRun],
  );

  const doSendVoice = useCallback(
    async (audio: Blob, filename?: string): Promise<void> => {
      const runId = (runSeq.current += 1);
      const userBubbleId = nextId.current();
      // Optimistic placeholder; replaced with the transcript once STT resolves.
      setFeed((prev) => [...prev, { id: userBubbleId, kind: 'user', text: '🎤 …' }]);
      setActiveRun({
        runId,
        stage: 'recognizing',
        inputKind: 'voice',
        transcript: '',
        replyId: null,
        error: null,
      });
      setError(null);
      try {
        const res =
          filename !== undefined ? await sendVoice(audio, filename) : await sendVoice(audio);
        const transcript = res.content.trim();
        setFeed((prev) =>
          prev.map((m) =>
            m.id === userBubbleId && m.kind === 'user'
              ? { ...m, text: transcript.length > 0 ? transcript : '🎤 (не расслышал)' }
              : m,
          ),
        );
        setActiveRun((r) =>
          r !== null && r.runId === runId ? { ...r, stage: 'thinking', transcript } : r,
        );
        const reply = await awaitAgentReply(res.eventId);
        if (reply !== null) finishRun(runId, reply.id);
      } catch (e) {
        const err = toError(e);
        setError(err);
        failRun(runId, err.message);
      }
    },
    [awaitAgentReply, finishRun, failRun],
  );

  const doSendVoiceNative = useCallback(
    async (transcript: string): Promise<void> => {
      const trimmed = transcript.trim();
      if (trimmed.length === 0) return;
      const runId = (runSeq.current += 1);
      setFeed((prev) => [...prev, makeOptimisticUser(nextId.current(), trimmed)]);
      // Голосовой прогон, но текст уже распознан на устройстве — сразу 'thinking'.
      setActiveRun({
        runId,
        stage: 'thinking',
        inputKind: 'voice',
        transcript: trimmed,
        replyId: null,
        error: null,
      });
      setError(null);
      try {
        const res = await sendText({ text: trimmed, source: 'voice_native' });
        const reply = await awaitAgentReply(res.eventId);
        if (reply !== null) finishRun(runId, reply.id);
      } catch (e) {
        const err = toError(e);
        setError(err);
        failRun(runId, err.message);
      }
    },
    [awaitAgentReply, finishRun, failRun],
  );

  const pickClarifyOption = useCallback(
    async (msgId: string, option: AIClarifyOption): Promise<void> => {
      // Mark the originating AIClarify bubble resolved so it renders disabled.
      setFeed((prev) =>
        prev.map((m) =>
          m.id === msgId && m.kind === 'ai_clarify'
            ? { ...m, resolvedOptionId: option.id }
            : m,
        ),
      );
      await doSendText(option.label);
    },
    [doSendText],
  );

  return {
    feed,
    loading,
    error,
    activeRun,
    refresh,
    sendText: doSendText,
    sendVoice: doSendVoice,
    sendVoiceNative: doSendVoiceNative,
    pickClarifyOption,
    clearRun,
  };
}
